import type { ProductSummary } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { formatPrice, STATUS_LABELS } from '@/entities/product';
import { formatDateTime } from '@/shared/lib';
import { Button, StatusBadge, tableStyles } from '@/shared/ui';

interface ProductRowProps {
  readonly product: ProductSummary;
  readonly isPublishPending: boolean;
  readonly isHidePending: boolean;
  readonly isDeletePending: boolean;
  readonly onPublish: (id: string, name: string) => void;
  readonly onHide: (id: string) => void;
  readonly onDeleteDraft: (id: string, name: string) => void;
}

/** 상품 목록의 한 행 — 요약 표시와 발행·숨김·삭제 액션. 추천 순위는 랜딩 관리의 추천 상품 블록이 맡는다. */
export const ProductRow = ({
  product,
  isPublishPending,
  isHidePending,
  isDeletePending,
  onPublish,
  onHide,
  onDeleteDraft,
}: ProductRowProps): ReactElement => {
  const navigate = useNavigate();
  const status = STATUS_LABELS[product.status];

  return (
    <tr className={tableStyles.row}>
      <td className={tableStyles.td}>
        <span className="flex items-center gap-3">
          {product.thumbnail ? (
            <img
              src={product.thumbnail.url}
              alt=""
              width={40}
              height={40}
              className="size-11 rounded-lg object-cover"
            />
          ) : (
            <span className="size-11 rounded-lg bg-muted" aria-hidden />
          )}
          <span className="flex flex-col">
            <span className="font-medium">{product.name}</span>
            {product.primaryCategoryName ? (
              <span className="text-xs text-muted-foreground">{product.primaryCategoryName}</span>
            ) : null}
          </span>
        </span>
      </td>
      <td className={tableStyles.td}>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </td>
      <td className={tableStyles.td}>{formatPrice(product.baseUnitPrice)}</td>
      <td className={tableStyles.td}>{product.minOrderQty.toLocaleString('ko-KR')}</td>
      <td className={tableStyles.td}>{formatDateTime(product.updatedAt)}</td>
      <td className={tableStyles.td}>
        <span className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/products/editor/${product.id}`)}
          >
            수정
          </Button>
          {product.status === 'PUBLISHED' ? (
            <Button
              variant="outline"
              size="sm"
              isLoading={isHidePending}
              onClick={() => onHide(product.id)}
            >
              숨김
            </Button>
          ) : (
            <Button
              size="sm"
              isLoading={isPublishPending}
              onClick={() => onPublish(product.id, product.name)}
            >
              발행
            </Button>
          )}
          {product.status === 'DRAFT' ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              isLoading={isDeletePending}
              onClick={() => onDeleteDraft(product.id, product.name)}
            >
              삭제
            </Button>
          ) : null}
        </span>
      </td>
    </tr>
  );
};
